import { jsPDF } from 'jspdf';

import type { Asset } from '../../assets/types/Asset';
import { CONSULTATION_PDF_FONT_FAMILY, applyConsultationPdfFonts } from './consultationPdfFont';
import type { CalendarEvent } from '../../calendar/types/Calendar';
import type { Contact } from '../../contacts/types/Contact';
import {
  DEFAULT_CONSULTATION_SECTION_KEY,
  groupStructuredConsultationFields,
  humanizeConsultationSectionKey,
  type StructuredConsultationField,
} from '../consultationUtils';
import { parseAttachmentsValue } from '../consultationAttachmentsSchema';
import {
  parseConsultationRepeaterSchema,
  parseRepeaterValue,
  type ConsultationRepeaterFieldSchema,
  type ConsultationRepeaterSchema,
} from '../consultationRepeaterSchema';
import { parseVoiceValue } from '../consultationVoiceSchema';
import type { Consultation, ConsultationFieldOption, ConsultationTemplate } from '../types/Consultation';
import {
  formatLongDateLabel,
  formatLocalizedDateTimeLabel,
  formatTimeInputLabel,
} from '../../../shared/utils/localization';

export type ConsultationExportLabels = {
  consultation: string;
  status: string;
  summary: string;
  notes: string;
  asset: string;
  contact: string;
  template: string;
  linkedEvent: string;
  structuredFields: string;
  exportedAt: string;
  generalSection: string;
};

export type ConsultationExportInput = {
  consultation: Consultation;
  title: string;
  status: string;
  summary: string;
  notes: string;
  asset?: Asset | null;
  contact?: Contact | null;
  template?: ConsultationTemplate | null;
  event?: CalendarEvent | null;
  fields: StructuredConsultationField[];
  fieldValues: Record<string, string>;
  labels: ConsultationExportLabels;
};

export type ConsultationExportDocument = {
  exportedAt: string;
  consultation: Consultation;
  context: {
    title: string;
    assetName?: string;
    contactName?: string;
    templateName?: string;
    linkedEventTitle?: string;
    linkedEventStartAt?: string;
    linkedEventEndAt?: string;
  };
  core: {
    status: string;
    summary: string;
    notes: string;
  };
  structuredFields: Array<{
    id: string;
    name: string;
    code?: string;
    type: string;
    section: string;
    required: boolean;
    value: string;
    rawValue?: string;
  }>;
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const sanitizeConsultationExportFileName = (title: string, consultationId: string) => {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const idSuffix = consultationId.slice(0, 8);
  return slug ? `consultation-${slug}-${idSuffix}` : `consultation-${idSuffix}`;
};

const resolveOptionLabel = (
  options: ConsultationFieldOption[] | undefined,
  optionId: string,
): string => {
  const matched = options?.find((option) => option.id === optionId);
  return matched?.label?.trim() || optionId;
};

const parseMultiSelectIds = (rawValue: string): string[] => {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map(String).map((item) => item.trim()).filter(Boolean);
    }
  } catch {
    // Comma-separated ids from the form UI.
  }

  return trimmed
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatDateLikeValue = (fieldType: string, rawValue: string): string => {
  if (fieldType === 'date') {
    try {
      return formatLongDateLabel(rawValue);
    } catch {
      return rawValue;
    }
  }

  if (fieldType === 'time') {
    return formatTimeInputLabel(rawValue) || rawValue;
  }

  if (fieldType === 'dateTime') {
    try {
      return formatLocalizedDateTimeLabel(rawValue);
    } catch {
      return rawValue;
    }
  }

  return rawValue;
};

const formatRepeaterScalarValue = (
  field: ConsultationRepeaterFieldSchema,
  value: unknown,
): string => {
  if (value == null || value === '') {
    return '';
  }

  switch (field.type) {
    case 'boolean':
      if (value === true || value === 'true') return 'Yes';
      if (value === false || value === 'false') return 'No';
      return String(value);
    case 'singleSelect':
      return resolveOptionLabel(field.options, String(value));
    case 'multiSelect': {
      const ids = Array.isArray(value)
        ? value.map(String)
        : parseMultiSelectIds(String(value));
      return ids.map((id) => resolveOptionLabel(field.options, id)).join(', ');
    }
    case 'date':
    case 'time':
    case 'dateTime':
      return formatDateLikeValue(field.type, String(value));
    default:
      return String(value);
  }
};

const REPEATER_MARKERS = ['•', '◦', '▪'] as const;

const formatRepeaterItem = (
  item: unknown,
  fields: ConsultationRepeaterFieldSchema[],
  depth = 0,
): string => {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return item == null ? '' : String(item);
  }

  const record = item as Record<string, unknown>;
  const bullet = REPEATER_MARKERS[Math.min(depth, REPEATER_MARKERS.length - 1)];
  const baseIndent = '  '.repeat(depth);
  const lines: string[] = [];

  const scalarFields = fields.filter((field) => field.type !== 'repeater');
  const nestedFields = fields.filter((field) => field.type === 'repeater');
  const renderedScalars: Array<{ label: string; valueLines: string[] }> = [];

  scalarFields.forEach((field) => {
    const label = field.label?.trim() || field.key;
    const formatted = formatRepeaterScalarValue(field, record[field.key]);
    if (!formatted) {
      return;
    }

    renderedScalars.push({
      label,
      valueLines: formatted.split(/\r?\n/),
    });
  });

  renderedScalars.forEach((entry, fieldIndex) => {
    const isPrimary = fieldIndex === 0;
    if (isPrimary) {
      lines.push(`${baseIndent}${bullet} ${entry.valueLines[0]}`);
      for (let index = 1; index < entry.valueLines.length; index += 1) {
        lines.push(`${baseIndent}  ${entry.valueLines[index]}`);
      }
      return;
    }

    const remainingNested = nestedFields.some((field) => {
      const nestedItems = Array.isArray(record[field.key]) ? record[field.key] as unknown[] : [];
      return nestedItems.length > 0;
    });
    const isLastScalar = fieldIndex === renderedScalars.length - 1 && !remainingNested;
    const branch = isLastScalar ? '└─' : '├─';
    const continuation = isLastScalar ? '   ' : '│  ';

    lines.push(`${baseIndent}  ${branch} ${entry.label}: ${entry.valueLines[0]}`);
    for (let index = 1; index < entry.valueLines.length; index += 1) {
      lines.push(`${baseIndent}  ${continuation}${entry.valueLines[index]}`);
    }
  });

  nestedFields.forEach((field) => {
    const nestedItems = Array.isArray(record[field.key]) ? record[field.key] as unknown[] : [];
    if (nestedItems.length === 0) {
      return;
    }

    if (lines.length > 0) {
      lines.push(`${baseIndent}  · · ·`);
    }

    nestedItems.forEach((nestedItem, nestedIndex) => {
      const nested = formatRepeaterItem(nestedItem, field.fields || [], depth + 1);
      if (!nested) {
        return;
      }

      if (nestedIndex > 0) {
        lines.push(`${baseIndent}  ········`);
      }

      lines.push(nested);
    });
  });

  return lines.join('\n');
};

const formatRepeaterValueForExport = (
  schema: ConsultationRepeaterSchema | null,
  rawValue: string,
): string => {
  if (!schema) {
    try {
      return JSON.stringify(JSON.parse(rawValue), null, 2);
    } catch {
      return rawValue;
    }
  }

  const parsed = parseRepeaterValue(schema, rawValue);
  const rootKey = schema.rootKey?.trim();
  const items = rootKey
    ? (Array.isArray(parsed[rootKey]) ? parsed[rootKey] as unknown[] : [])
    : (Array.isArray(parsed.items) ? parsed.items as unknown[] : [parsed]);

  if (items.length === 0) {
    return '';
  }

  const blocks = items
    .map((item) => formatRepeaterItem(item, schema.fields, 0))
    .filter(Boolean);

  if (blocks.length === 0) {
    return '';
  }

  if (blocks.length === 1) {
    return `╭────────────╮\n${blocks[0]}\n╰────────────╯`;
  }

  return [
    '╭────────────╮',
    ...blocks.flatMap((block, index) => (
      index === 0
        ? [block]
        : ['┊ · · · · · · ┊', block]
    )),
    '╰────────────╯',
  ].join('\n');
};

export const formatFieldValueForExport = (
  field: StructuredConsultationField,
  rawValue: string | undefined,
): string => {
  if (!rawValue?.trim()) {
    return '';
  }

  switch (field.fieldType) {
    case 'boolean':
      if (rawValue === 'true') return 'Yes';
      if (rawValue === 'false') return 'No';
      return rawValue;
    case 'singleSelect':
      return resolveOptionLabel(field.options, rawValue.trim());
    case 'multiSelect':
      return parseMultiSelectIds(rawValue)
        .map((id) => resolveOptionLabel(field.options, id))
        .join(', ');
    case 'date':
    case 'time':
    case 'dateTime':
      return formatDateLikeValue(field.fieldType, rawValue.trim());
    case 'attachments':
      try {
        const parsed = parseAttachmentsValue(rawValue);
        const names = parsed.attachments
          .map((file) => file.fileName?.trim() || file.id)
          .filter(Boolean);
        return names.length > 0 ? names.join(', ') : rawValue;
      } catch {
        return rawValue;
      }
    case 'voice':
      try {
        const parsed = parseVoiceValue(rawValue);
        if (parsed.transcription?.text?.trim()) {
          return parsed.transcription.text.trim();
        }
        if (parsed.audio?.durationSeconds != null) {
          return `Voice recording (${parsed.audio.durationSeconds}s)`;
        }
        return 'Voice recording';
      } catch {
        return rawValue;
      }
    case 'repeater':
      return formatRepeaterValueForExport(
        parseConsultationRepeaterSchema(field.schemaJson),
        rawValue,
      );
    default:
      return rawValue;
  }
};

export const buildConsultationExportDocument = (
  input: ConsultationExportInput,
): ConsultationExportDocument => {
  const sections = groupStructuredConsultationFields(input.fields);

  return {
    exportedAt: new Date().toISOString(),
    consultation: input.consultation,
    context: {
      title: input.title,
      assetName: input.asset?.name || input.consultation.assetName,
      contactName: input.contact?.name
        || input.consultation.contactName
        || input.consultation.customerContactName,
      templateName: input.template?.name || input.consultation.templateName,
      linkedEventTitle: input.event?.title || input.consultation.calendarEventTitle,
      linkedEventStartAt: input.event?.startAtUtc,
      linkedEventEndAt: input.event?.endAtUtc,
    },
    core: {
      status: input.status,
      summary: input.summary,
      notes: input.notes,
    },
    structuredFields: sections.flatMap((section) => section.fields.map((field) => {
      const rawValue = input.fieldValues[field.id];
      return {
        id: field.id,
        name: field.name,
        code: field.code,
        type: field.fieldType,
        section: section.label || humanizeConsultationSectionKey(section.sectionKey),
        required: field.assignmentRequired,
        value: formatFieldValueForExport(field, rawValue),
        rawValue: rawValue?.trim() ? rawValue : undefined,
      };
    })),
  };
};

export const exportConsultationToJson = (input: ConsultationExportInput) => {
  const document = buildConsultationExportDocument(input);
  const blob = new Blob([JSON.stringify(document, null, 2)], { type: 'application/json;charset=utf-8' });
  const fileName = `${sanitizeConsultationExportFileName(input.title, input.consultation.id)}.json`;
  triggerBlobDownload(blob, fileName);
};

const ensurePdfSpace = (doc: jsPDF, y: number, lineHeight: number, bottomMargin: number) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + lineHeight > pageHeight - bottomMargin) {
    doc.addPage();
    return 18;
  }

  return y;
};

const addPdfLabelValue = (
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  bottomMargin: number,
) => {
  let nextY = ensurePdfSpace(doc, y, lineHeight * 2, bottomMargin);
  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
  doc.setFontSize(10);
  doc.text(`${label}:`, x, nextY);
  nextY += lineHeight;

  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
  const sourceLines = String(value || '-').split(/\r?\n/);
  const lines = sourceLines.flatMap((line) => doc.splitTextToSize(line.length > 0 ? line : ' ', maxWidth));
  for (const line of lines) {
    nextY = ensurePdfSpace(doc, nextY, lineHeight, bottomMargin);
    doc.text(String(line), x, nextY);
    nextY += lineHeight;
  }

  return nextY + 4;
};

export const exportConsultationToPdf = async (input: ConsultationExportInput) => {
  const document = buildConsultationExportDocument(input);
  const labels = input.labels;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  await applyConsultationPdfFonts(doc);
  const left = 14;
  const right = 14;
  const maxWidth = doc.internal.pageSize.getWidth() - left - right;
  const lineHeight = 5;
  const bottomMargin = 16;
  let y = 18;

  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
  doc.setFontSize(16);
  doc.text(document.context.title || labels.consultation, left, y);
  y += 8;

  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90, 100, 110);
  y = ensurePdfSpace(doc, y, lineHeight, bottomMargin);
  doc.text(`${labels.exportedAt}: ${new Date(document.exportedAt).toLocaleString()}`, left, y);
  y += 8;
  doc.setTextColor(20, 30, 40);

  const metadataRows: Array<[string, string | undefined]> = [
    [labels.status, document.core.status],
    [labels.asset, document.context.assetName],
    [labels.contact, document.context.contactName],
    [labels.template, document.context.templateName],
    [labels.linkedEvent, document.context.linkedEventTitle],
  ];

  for (const [label, value] of metadataRows) {
    if (!value?.trim()) {
      continue;
    }

    y = addPdfLabelValue(doc, label, value, left, y, maxWidth, lineHeight, bottomMargin);
  }

  y = ensurePdfSpace(doc, y, lineHeight * 2, bottomMargin);
  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
  doc.setFontSize(12);
  doc.text(labels.summary, left, y);
  y += lineHeight + 1;
  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
  doc.setFontSize(10);
  const summaryLines = doc.splitTextToSize(document.core.summary || '-', maxWidth);
  for (const line of summaryLines) {
    y = ensurePdfSpace(doc, y, lineHeight, bottomMargin);
    doc.text(String(line), left, y);
    y += lineHeight;
  }

  y += 4;
  y = ensurePdfSpace(doc, y, lineHeight * 2, bottomMargin);
  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
  doc.setFontSize(12);
  doc.text(labels.notes, left, y);
  y += lineHeight + 1;
  doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
  doc.setFontSize(10);
  const notesLines = doc.splitTextToSize(document.core.notes || '-', maxWidth);
  for (const line of notesLines) {
    y = ensurePdfSpace(doc, y, lineHeight, bottomMargin);
    doc.text(String(line), left, y);
    y += lineHeight;
  }

  const structuredFieldSections = groupStructuredConsultationFields(input.fields);
  const structuredFieldValuesById = new Map(
    document.structuredFields.map((field) => [field.id, field.value]),
  );

  if (structuredFieldSections.some((section) => section.fields.length > 0)) {
    y += 4;
    y = ensurePdfSpace(doc, y, lineHeight * 2, bottomMargin);
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
    doc.setFontSize(12);
    doc.text(labels.structuredFields, left, y);
    y += lineHeight + 2;

    const showSectionHeadings = structuredFieldSections.length > 1
      || structuredFieldSections.some((section) => section.sectionKey !== DEFAULT_CONSULTATION_SECTION_KEY);

    for (const section of structuredFieldSections) {
      if (section.fields.length === 0) {
        continue;
      }

      if (showSectionHeadings) {
        const sectionLabel = section.label || labels.generalSection;
        y = ensurePdfSpace(doc, y, lineHeight * 2, bottomMargin);
        doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
        doc.setFontSize(11);
        doc.text(sectionLabel, left, y);
        y += lineHeight + 1;
      }

      for (const field of section.fields) {
        const value = structuredFieldValuesById.get(field.id) ?? '';
        y = addPdfLabelValue(
          doc,
          field.name,
          value || '-',
          left,
          y,
          maxWidth,
          lineHeight,
          bottomMargin,
        );
      }
    }
  }

  const fileName = `${sanitizeConsultationExportFileName(input.title, input.consultation.id)}.pdf`;
  doc.save(fileName);
};
